import { G } from '@mobily/ts-belt';
import { EditInDialogButtonProps } from '@react-admin/ra-form-layout';
import { useCallback, useEffect, useMemo } from 'react';
import {
  BooleanInput,
  required,
  SelectInput,
  TextInput,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { GetB2bAdminsOneResponse, PutB2bAdminsIdEndpoint } from '@Common/types/bo/b2b/admins';
import {
  B2bCompanyDocumentDetails,
  GetB2bCompanyDetailsResponse,
} from '@Common/types/bo/b2b/companies';
import { validateEmailWithDomains } from '@Common/validators/email_matching_domain_validation';

import { adminRoleChoices, languagePreferenceChoices } from '@sh4res/bo-common/choices/admins';
import {
  DeleteButton,
  EditInDialogButton,
  StepperForm,
  StepperFormStep,
  TypographyTranslated,
} from '@sh4res/bo-common/components';
import { AdminConfirmationStep } from '@sh4res/bo-common/components/B2bAdmins/AdminConfirmationStep';
import { AdminCorrespondenceStep } from '@sh4res/bo-common/components/B2bAdmins/AdminCorrespondenceStep';
import {
  ThirdPartyMandateFields,
  transformThirdPartyMandate,
} from '@sh4res/bo-common/components/B2bAdmins/ThirdPartyMandateFields';
import { validateAdminEmails } from '@sh4res/bo-common/components/B2bAdmins/validateAdminEmails';
import { genderChoices } from '@sh4res/bo-common/components/B2bEmployee/choices';
import { PhoneNumberInput } from '@sh4res/bo-common/fields';
import { useResourceBasePath, useUploadPresignedUrl } from '@sh4res/bo-common/hooks';
import { getErrorMessage, getSpaceAboveFooter, inputDisabledProps } from '@sh4res/bo-common/utils';
import { isB2bAdmin } from '@sh4res/bo-common/utils/b2b_auth_role';
import { isBoExternal } from '@sh4res/bo-common/utils/env';
import { trimmedEmail } from '@sh4res/bo-common/validators';

export const B2bAdminEdit = (
  props: EditInDialogButtonProps['ButtonProps'] & { isRestrictedModification?: boolean },
) => {
  const { isRestrictedModification } = props;
  const { data: identity } = useGetIdentity();
  const record = useRecordContext<GetB2bAdminsOneResponse>();
  const resourceBasePath = useResourceBasePath();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const { upload } = useUploadPresignedUrl();

  const validateAdminEmailsField = useCallback(
    (_value: unknown, allValues: { email?: string; secondary_email?: string | null }) => {
      const result = validateAdminEmails(allValues, record);
      if (!result.isValid) {
        return translate(result.message as string);
      }
      return undefined;
    },
    [record, translate],
  );

  // Prefill the mandate document input with the admin's existing document, so it is
  // displayed and preserved unless the user explicitly replaces or removes it.
  const { data: mandateDocument } = useGetOne<B2bCompanyDocumentDetails>(
    `${resourceBasePath}companies/documents`,
    { id: record?.third_party_mandate_document_id ?? 0 },
    { enabled: G.isNotNullable(record?.third_party_mandate_document_id) },
  );

  const onError = useCallback(
    (error: unknown) => {
      notify('resources.bo_common.b2b.admins.actions.edit.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    },
    [notify],
  );

  const onSuccess = useCallback(
    (response: PutB2bAdminsIdEndpoint['Client']['ResponseBody']) => {
      notify('resources.bo_common.b2b.admins.actions.edit.success', { type: 'success' });
      refresh();
      redirect(`${resourceBasePath}companies/${response.company_id}/show?tab=admins`);
    },
    [notify, redirect, refresh, resourceBasePath],
  );

  const formRecord = useMemo(
    () =>
      record
        ? {
            ...record,
            is_active: record.status !== 'inactive',
            third_party_mandate_document: mandateDocument
              ? {
                  id: mandateDocument.id,
                  src: mandateDocument.document_url,
                  title: mandateDocument.document_name,
                }
              : null,
          }
        : undefined,
    [record, mandateDocument],
  );

  const spaceAboveFooter = getSpaceAboveFooter();

  const companyId = record?.company_id;

  if (G.isNullable(record) || G.isNullable(companyId)) {
    return null;
  }

  const isConnectedAdminAccount = isB2bAdmin(identity) && identity.id === record.id;

  const deleteDialogBody = translate('resources.bo_common.b2b.admins.actions.delete.content', {
    adminId: record.id,
    email: record.email,
  });

  const testId = 'BoExternalB2bAdminEdit';

  const steps: StepperFormStep[] = [
    {
      label: 'resources.bo_common.b2b.admins.steps.general',
      hideDraft: true,
      content: (
        <>
          <TextInput
            data-testid={`${testId}_RoleInCompanyInput`}
            source="role_in_company"
            {...inputDisabledProps(isRestrictedModification ?? false)}
          />
          <EmailAndThirdPartySection
            companyId={companyId}
            isConnectedAdminAccount={isConnectedAdminAccount}
            isRestrictedModification={isRestrictedModification}
            validateAdminEmailsField={validateAdminEmailsField}
          />
          <BooleanInput
            data-testid={`${testId}_IsActiveInput`}
            source="is_active"
            {...inputDisabledProps(isConnectedAdminAccount || (isRestrictedModification ?? false))}
          />
          <RoleSelectInput
            isConnectedAdminAccount={isConnectedAdminAccount}
            testId={`${testId}_RoleSelectInput`}
          />
          <ThirdPartyMandateFields testId={testId} />
          <SelectInput
            data-testid={`${testId}_GenderInput`}
            source="gender"
            choices={genderChoices}
            {...inputDisabledProps(isRestrictedModification ?? false)}
          />
          <TextInput
            data-testid={`${testId}_FirstnameInput`}
            source="firstname"
            validate={[required()]}
            isRequired
            {...inputDisabledProps(isRestrictedModification ?? false)}
          />
          <TextInput
            data-testid={`${testId}_LastnameInput`}
            source="lastname"
            validate={[required()]}
            isRequired
            {...inputDisabledProps(isRestrictedModification ?? false)}
          />
          <PhoneNumberField testId={`${testId}_PhoneInput`} />
          {!isBoExternal() && (
            <TextInput
              data-testid={`${testId}_SecondaryEmailInput`}
              source="secondary_email"
              validate={[trimmedEmail(), validateAdminEmailsField]}
            />
          )}
          <SelectInput
            data-testid={`${testId}_LanguageInput`}
            source="language_preference"
            choices={languagePreferenceChoices}
            defaultValue={'FR'}
            validate={[required()]}
            isRequired
            {...inputDisabledProps(isRestrictedModification ?? false)}
          />
        </>
      ),
    },
    {
      label: 'resources.bo_common.b2b.admins.steps.addresses',
      hideDraft: true,
      content: (
        <AdminCorrespondenceStep testId={testId} disabled={isRestrictedModification ?? false} />
      ),
    },
    {
      label: 'resources.bo_common.b2b.admins.steps.confirmation',
      hideDraft: true,
      content: <AdminConfirmationStep testId={testId} />,
    },
  ];

  return (
    <EditInDialogButton
      testId={`${testId}_OpenButton`}
      resource={`${resourceBasePath}admins`}
      fullWidth
      maxWidth="md"
      title="resources.bo_common.b2b.admins.actions.edit.title"
      ButtonProps={{
        variant: 'contained',
        color: 'info',
        size: 'small',
        sx: {
          fontWeight: 'bold',
          textTransform: 'none',
          position: 'fixed',
          bottom: spaceAboveFooter,
          right: '75px',
        },
        ...props,
      }}
      mutationMode="pessimistic"
      transform={async (data: PutB2bAdminsIdEndpoint['Client']['RequestBody']) =>
        transformThirdPartyMandate(data, upload)
      }
      // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error - Can't make onSuccess typing work
      mutationOptions={{ onSuccess, onError }}>
      <StepperForm
        steps={steps}
        defaultValues={formRecord}
        permissions={{
          resource: `${resourceBasePath}admins`,
          action: 'edit',
        }}
        toolbarExtras={
          !isBoExternal() && (
            <DeleteButton
              testId={`${testId}_DeleteButton`}
              redirect={`/b2b/companies/${companyId}/show?tab=admins`}
              mutationMode="pessimistic"
              confirmTitle="resources.bo_common.b2b.admins.actions.delete.title"
              confirmContent={deleteDialogBody}
              permissions={{
                resource: 'b2b/admins',
                action: 'delete',
              }}
            />
          )
        }
        testId={testId}
      />
    </EditInDialogButton>
  );
};

function getRoleChoices(isThirdParty: boolean, isConnectedAdminAccount: boolean) {
  if (isThirdParty) {
    // bo_external third parties can only be level 1, the internal BO can also set level 3
    return adminRoleChoices.filter((choice) =>
      isBoExternal()
        ? choice.id === 'admin_level_1'
        : choice.id === 'admin_level_1' || choice.id === 'admin_level_3',
    );
  }
  if (isBoExternal() && !isConnectedAdminAccount) {
    return adminRoleChoices.filter((choice) => choice.id !== 'admin_level_4');
  }
  return adminRoleChoices;
}

function PhoneNumberField({ testId }: { testId: string }) {
  // Phone number is mandatory for third party admins, but on edition it can only be enforced
  // when the admin already has one, so existing admins without a phone can still be edited.
  const isThirdParty = useWatch({ name: 'is_third_party' });
  const record = useRecordContext<GetB2bAdminsOneResponse>();
  const isRequired = isThirdParty && Boolean(record?.phone_number);

  return (
    <PhoneNumberInput
      testId={testId}
      source="phone_number"
      mobileOnly
      validate={isRequired ? [required()] : []}
      isRequired={isRequired}
    />
  );
}

function RoleSelectInput({
  isConnectedAdminAccount,
  testId,
}: {
  isConnectedAdminAccount: boolean;
  testId: string;
}) {
  const isThirdParty = useWatch({ name: 'is_third_party' });
  const role = useWatch({ name: 'role' });
  const { setValue } = useFormContext();

  useEffect(() => {
    if (isThirdParty && role !== 'admin_level_1' && role !== 'admin_level_3') {
      setValue('role', 'admin_level_1');
    }
  }, [isThirdParty, role, setValue]);

  return (
    <SelectInput
      source="role"
      validate={[required()]}
      isRequired
      data-testid={testId}
      choices={getRoleChoices(isThirdParty, isConnectedAdminAccount)}
      {...inputDisabledProps(isConnectedAdminAccount)}
    />
  );
}

function EmailAndThirdPartySection({
  companyId,
  isConnectedAdminAccount,
  isRestrictedModification,
  validateAdminEmailsField,
}: {
  companyId: number;
  isConnectedAdminAccount: boolean;
  isRestrictedModification?: boolean;
  validateAdminEmailsField: (
    value: unknown,
    allValues: { email?: string; secondary_email?: string | null },
  ) => string | undefined;
}) {
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();
  const { trigger } = useFormContext();
  const isThirdParty = useWatch({ name: 'is_third_party' });
  const secondaryEmail = useWatch({ name: 'secondary_email' });

  useEffect(() => {
    void trigger('email');
  }, [isThirdParty, trigger]);

  useEffect(() => {
    void trigger('email');
    void trigger('secondary_email');
  }, [secondaryEmail, trigger]);

  const { data: companyDetails } = useGetOne<GetB2bCompanyDetailsResponse>(
    `${resourceBasePath}companies`,
    {
      id: companyId,
    },
    {
      enabled: G.isNotNullable(companyId),
    },
  );

  if (G.isNullable(companyDetails)) {
    return;
  }

  return (
    <>
      <TextInput
        source="email"
        data-testid="BoExternalB2bAdminEdit_EmailInput"
        validate={[
          required(),
          trimmedEmail(),
          validateAdminEmailsField,
          ...(!isThirdParty
            ? [
                validateEmailWithDomains(
                  companyDetails.domains,
                  translate(
                    'resources.bo_common.b2b.companies.sections.legal_representative.fields.error_email_domains',
                    { domains: companyDetails.domains },
                  ),
                ),
              ]
            : []),
        ]}
        isRequired
        {...inputDisabledProps(isConnectedAdminAccount)}
      />
      <BooleanInput
        source="is_third_party"
        defaultValue={false}
        data-testid="BoExternalB2bAdminEdit_IsThirdPartyInput"
        {...inputDisabledProps(isRestrictedModification ?? false)}
      />
      {isThirdParty && (
        <TypographyTranslated
          variant="body2"
          label="resources.bo_common.b2b.admins.third_party_disclaimer"
        />
      )}
    </>
  );
}
